/**
 * Centralized Design Tokens
 *
 * This file contains all design tokens used throughout the application.
 * These tokens should be used instead of hardcoded values to ensure
 * consistency and make future redesigns easier.
 */

// ============================================================================
// COLOR TOKENS
// ============================================================================

export const colorTokens = {
  // Neutral colors (most commonly used)
  neutral: {
    white: '#FFFFFF',
    gray50: '#FAFAFA',
    gray100: '#F5F5F5',
    gray200: '#E0E0E0',
    gray300: '#C4C4C4',
    gray400: '#969595',
    gray500: '#757575',
    gray600: '#666666',
    gray700: '#3E4345',
    gray800: '#333333',
    gray900: '#131417',
  },

  // Semantic colors
  semantic: {
    success: '#2E7D32',
    warning: '#ED6C02',
    error: '#842129',
    info: '#0037C2',
  },

  // Background colors
  background: {
    primary: '#FFFFFF',
    secondary: '#F7F9FA',
    tertiary: '#F1F3F4',
    disabled: '#F5F5F5',
    overlay: 'rgba(0, 0, 0, 0.5)',
  },

  // Border colors
  border: {
    light: '#E0E0E0',
    medium: '#C6CACC',
    dark: '#919699',
    focus: '#0037C2',
  },

  // Interactive colors
  interactive: {
    primary: '#3E4345',
    primaryHover: '#555555',
    secondary: '#E3E5E6',
    secondaryHover: '#C6CACC',
  },
} as const;

// ============================================================================
// TYPOGRAPHY TOKENS
// ============================================================================

export const typographyTokens = {
  // Font sizes (using rem for accessibility)
  fontSize: {
    xs: '0.5rem', // 8px
    sm: '0.65rem', // 10px
    base: '0.75rem', // 12px
    md: '0.875rem', // 14px
    lg: '1rem', // 16px
    xl: '1.125rem', // 18px
    '2xl': '1.25rem', // 20px
    '3xl': '1.5rem', // 24px
    '4xl': '2rem', // 32px
    '5xl': '3rem', // 48px
  },

  // Font weights
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const;

// ============================================================================
// SPACING TOKENS
// ============================================================================

export const spacingTokens = {
  // Base spacing unit (8px)
  base: 8,

  // Spacing scale
  xs: '4px', // 0.5 * base
  sm: '8px', // 1 * base
  md: '16px', // 2 * base
  lg: '24px', // 3 * base
  xl: '32px', // 4 * base
  '2xl': '48px', // 6 * base
  '3xl': '64px', // 8 * base
} as const;

// ============================================================================
// BORDER RADIUS TOKENS
// ============================================================================

export const borderRadiusTokens = {
  none: '0px',
  sm: '2px',
  base: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  full: '9999px',
} as const;

// ============================================================================
// SHADOW TOKENS
// ============================================================================

export const shadowTokens = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
} as const;

// ============================================================================
// COMMON STYLE MIXINS
// ============================================================================

export const styleMixins = {
  // Flexbox patterns
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  flexBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  flexColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
  },

  // Common button styles
  buttonPrimary: {
    backgroundColor: colorTokens.interactive.primary,
    color: colorTokens.neutral.white,
    fontWeight: typographyTokens.fontWeight.semibold,
    textTransform: 'none' as const,
    borderRadius: borderRadiusTokens.base,
    '&:hover': {
      backgroundColor: colorTokens.interactive.primaryHover,
    },
  },

  buttonSecondary: {
    backgroundColor: colorTokens.interactive.secondary,
    color: colorTokens.neutral.gray700,
    fontWeight: typographyTokens.fontWeight.medium,
    textTransform: 'none' as const,
    borderRadius: borderRadiusTokens.base,
    '&:hover': {
      backgroundColor: colorTokens.interactive.secondaryHover,
    },
  },

  // Common container styles
  containerPadded: {
    padding: spacingTokens.md,
  },

  containerBordered: {
    border: `1px solid ${colorTokens.border.light}`,
    borderRadius: borderRadiusTokens.base,
  },

  // Text truncation
  textTruncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },

  // Disabled state
  disabled: {
    backgroundColor: colorTokens.background.disabled,
    color: colorTokens.neutral.gray400,
    cursor: 'not-allowed',
  },
} as const;

// ============================================================================
// COMPONENT-SPECIFIC TOKENS
// ============================================================================

export const componentTokens = {
  // Form components
  input: {
    backgroundColor: colorTokens.background.secondary,
    borderColor: colorTokens.border.light,
    borderRadius: borderRadiusTokens.base,
    padding: `${spacingTokens.sm} ${spacingTokens.md}`,
  },

  // Card components
  card: {
    backgroundColor: colorTokens.background.primary,
    borderRadius: borderRadiusTokens.md,
    boxShadow: shadowTokens.base,
    padding: spacingTokens.lg,
  },

  // Modal/Dialog components
  modal: {
    backgroundColor: colorTokens.background.primary,
    borderRadius: borderRadiusTokens.lg,
    boxShadow: shadowTokens.xl,
    maxWidth: '600px',
  },
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get spacing value by multiplying base unit
 * @param multiplier - Number to multiply base spacing by
 * @returns Spacing value in pixels
 */
export const getSpacing = (multiplier: number): string => {
  return `${spacingTokens.base * multiplier}px`;
};

/**
 * Create consistent gap values for flex containers
 * @param size - Size token key
 * @returns Gap value
 */
export const getGap = (size: keyof typeof spacingTokens): string => {
  if (size === 'base') return `${spacingTokens.base}px`;
  return spacingTokens[size];
};

// ============================================================================
// EXPORTS
// ============================================================================

export const designTokens = {
  colors: colorTokens,
  typography: typographyTokens,
  spacing: spacingTokens,
  borderRadius: borderRadiusTokens,
  shadows: shadowTokens,
  mixins: styleMixins,
  components: componentTokens,
} as const;

export default designTokens;
